import { useEffect, useState } from 'react';
import { TrashIcon, PencilSquareIcon, XMarkIcon, LinkIcon } from '@heroicons/react/24/solid';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import ConfirmModal from 'shared_items/ConfirmModal';
import DatePicker from 'shared_items/DatePicker';
import DropdownWithInput from 'shared_items/DropdownWithInput';
import { formatDate } from 'shared_items/formatConventions';
import DropdownType, { TYPE_COLORS } from '../moduleComponents/DropdownType';
import { createHealthRecord, updateHealthRecord, deleteHealthRecord } from '../health_services';

const emptyForm = () => ({ date: '', type: '', area: '', professional: '', professional_ref: null, description: '' });

const formFromRecord = (r) => ({
  date: r.date || '',
  type: r.type || '',
  area: r.area || '',
  professional: r.professional || '',
  professional_ref: r.professional_ref ?? null,
  description: r.description || '',
});

/**
 * Unified record modal: create (record=null), view + edit existing (record object).
 * Mirrors travel/tab_trips/viewComponents/ModalTripForm.jsx shell — compact header
 * with icon actions, view/edit toggle in a single component, footer actions on a
 * border-t strip, deletion via ConfirmModal (never window.confirm).
 */
const ModalRecordForm = ({ record, areas = [], professionals = [], onClose, onSaved, onDeleted }) => {
  const isCreate = record === null;
  const [isEditMode, setIsEditMode] = useState(isCreate);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    setShowDeleteConfirm(false);
    if (isCreate) {
      setIsEditMode(true);
      setFormData(emptyForm());
    } else {
      setIsEditMode(false);
      setFormData(formFromRecord(record));
    }
  }, [record]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (e) => setFormData((prev) => ({ ...prev, date: e.target.value }));
  const handleTypeChange = (value) => setFormData((prev) => ({ ...prev, type: value }));
  const handleAreaChange = (value) => setFormData((prev) => ({ ...prev, area: value }));

  const professionalNames = professionals.filter((p) => p.is_active).map((p) => p.name);

  // Soft-link handling: selecting a suggestion sets professional_ref; typing
  // anything that doesn't exactly match a known professional unlinks it. The
  // backend never infers this by string match — only this explicit UI flow sets it.
  const handleProfessionalChange = (value) => {
    const matched = professionals.find((p) => p.name === value && p.is_active);
    setFormData((prev) => ({
      ...prev,
      professional: value,
      professional_ref: matched ? matched.id : null,
    }));
  };

  const handleEditToggle = () => {
    setFormData(formFromRecord(record));
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (isCreate) {
      onClose();
    } else {
      setFormData(formFromRecord(record));
      setIsEditMode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (isCreate) {
        const response = await createHealthRecord(formData);
        toast.success('Record created successfully!');
        onSaved(response.data);
      } else {
        const response = await updateHealthRecord(record.id, formData);
        toast.success('Record updated successfully!');
        onSaved(response.data);
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save record');
      console.error('Failed to save health record:', error.response?.data || error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setIsSubmitting(true);
    try {
      await deleteHealthRecord(record.id);
      toast.success('Record deleted successfully!');
      onDeleted(record.id);
      onClose();
    } catch (error) {
      toast.error('Failed to delete record');
      console.error('Failed to delete health record:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const headerTitle = isCreate ? 'Add Record' : isEditMode ? 'Edit Record' : 'Record Details';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col z-10 mx-2 text-sm">

        {/* Header — compact, icon actions (matches ModalTripForm) */}
        <div className="flex items-center justify-between px-2 py-1 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-800 pr-2 truncate leading-none">{headerTitle}</h2>
          <div className="flex items-center gap-0 flex-shrink-0">
            {!isCreate && !isEditMode && (
              <button type="button" onClick={handleEditToggle} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg" title="Edit">
                <PencilSquareIcon className="h-5 w-5" />
              </button>
            )}
            <button type="button" onClick={onClose} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg" title="Close">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        {!isEditMode && !isCreate ? (
          <div className="flex-1 overflow-y-auto p-3 text-xs space-y-3">
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-100 w-fit">
              <CalendarIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <strong className="text-gray-800">{formatDate(record.date)}</strong>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1 text-xs text-white rounded-full ${TYPE_COLORS[record.type] || 'bg-gray-400'}`}>
                {record.type}
              </span>
              <span className="px-3 py-1 bg-gray-700 text-white text-xs rounded-full">{record.area}</span>
            </div>
            <div>
              <span className="block font-medium text-gray-600">Professional</span>
              <p className="text-gray-900 mt-0.5 flex items-center gap-1.5">
                {record.professional}
                {record.professional_ref_detail && (
                  <LinkIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" title="Linked to a registered professional" />
                )}
              </p>
              {record.professional_ref_detail?.location && (
                <p className="text-gray-500 text-[11px] mt-0.5">{record.professional_ref_detail.location}</p>
              )}
            </div>
            <div>
              <span className="block font-medium text-gray-600">Description</span>
              <p className="text-gray-800 whitespace-pre-line mt-0.5">{record.description?.trim() || 'No description'}</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
            <div className="flex-1 overflow-y-auto p-3 space-y-3 text-xs">
              <div>
                <label className="block font-medium text-gray-700 mb-1">Date</label>
                <DatePicker
                  value={formData.date}
                  onChange={handleDateChange}
                  mode="single"
                  colorScheme="blue"
                  placeholder="Select date"
                  className="rounded-lg w-full bg-gray-100 hover:bg-gray-200 border-0"
                  dropdownZIndex={60}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Type</label>
                  <DropdownType value={formData.type} onChange={handleTypeChange} />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Area</label>
                  <DropdownWithInput
                    value={formData.area}
                    onChange={handleAreaChange}
                    options={areas}
                    placeholder="Type or select area"
                  />
                </div>
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                  Professional
                  {formData.professional_ref && (
                    <LinkIcon className="h-3.5 w-3.5 text-gray-400" title="Linked to a registered professional" />
                  )}
                </label>
                <DropdownWithInput
                  value={formData.professional}
                  onChange={handleProfessionalChange}
                  options={professionalNames}
                  placeholder="Type or select professional"
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={5}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>

            <div className="flex justify-between items-center gap-2 px-3 py-2 border-t border-gray-200 flex-shrink-0 bg-white">
              {!isCreate && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                  disabled={isSubmitting}
                >
                  Delete
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        )}

        {showDeleteConfirm && !isSubmitting && (
          <ConfirmModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            title="Delete Record"
            message="Are you sure you want to delete this record? This action is irreversible."
            confirmText="Delete"
            cancelText="Cancel"
            overlayClassName="z-[70]"
          />
        )}
      </div>
    </div>
  );
};

export default ModalRecordForm;
